import React, { useState, useEffect, useMemo } from "react";
import "./ServiceCart.css";
import { MDBBadge, MDBBtn, MDBIcon } from "../../mdbCompat";
import axios from "axios";

// how many services a customer must complete in the current month to trigger the congrats popup
const MONTHLY_GOAL = 60;

// how many distinct service types (beauty / animal / parking / ...) trigger the "multi-service" alert
const MULTI_SERVICE_THRESHOLD = 2;

// ---- shared field readers (kept in one place since field names can vary) ----
function getAmount(item) {
  return Number(item.amount ?? item.payamount ?? item.amountPaid ?? 0);
}

function getItemDate(item) {
  const raw = item.date || item.createdDate || item.entryTime || item.requestDate;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d) ? null : d;
}

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key) {
  const [year, month] = key.split("-").map(Number);
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// -----------------------------------------------------------------
// Groups the raw request list into one stats object per customer.
// NOTE: amount/date field names are guessed (amount, payamount, date,
// createdDate, entryTime). Adjust the fallbacks below to match your
// actual API response field names if they differ.
// -----------------------------------------------------------------
function buildCustomerStats(data) {
  const now = new Date();
  const map = {};

  data.forEach((item) => {
    const customer = item.userName || item.personalId || "Unknown";
    const serviceType = item.serviceName || "Unknown";
    const amount = getAmount(item);
    const itemDate = getItemDate(item);
    const isCompleted = item.requeststatus === 3;

    if (!map[customer]) {
      map[customer] = {
        customer,
        personalId: item.personalId,
        serviceTypes: new Set(),
        totalServices: 0,
        completedServices: 0,
        thisMonthCompleted: 0,
        totalIncome: 0,
      };
    }

    const bucket = map[customer];
    bucket.serviceTypes.add(serviceType);
    bucket.totalServices += 1;

    if (isCompleted) {
      bucket.completedServices += 1;
      bucket.totalIncome += amount;

      if (
        itemDate &&
        itemDate.getMonth() === now.getMonth() &&
        itemDate.getFullYear() === now.getFullYear()
      ) {
        bucket.thisMonthCompleted += 1;
      }
    }
  });

  return Object.values(map).map((b) => ({
    ...b,
    serviceTypeCount: b.serviceTypes.size,
    serviceTypesList: Array.from(b.serviceTypes),
  }));
}

// picks a small emoji for the service type so cards are scannable at a glance
function getServiceIcon(serviceName) {
  const s = (serviceName || "").toLowerCase();
  if (s.includes("beauty")) return "💄";
  if (s.includes("animal") || s.includes("pet")) return "🐾";
  if (s.includes("park")) return "🅿️";
  if (s.includes("plumb")) return "🔧";
  if (s.includes("electric")) return "💡";
  if (s.includes("ac") || s.includes("repair")) return "🛠️";
  return "🧾";
}

// initials shown inside the customer avatar circle
function getInitials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

// deterministic avatar colour so the same customer always gets the same colour
const AVATAR_COLORS = ["#0f6d66", "#a3620a", "#5b6bd6", "#c23b3b", "#1c8a4f", "#8a5bc2"];
function getAvatarColor(name) {
  const safeName = name || "?";
  const code = safeName.split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

function ServiceCart(props) {
  const { data = [] } = props;

  const [showModal, setShowModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [otp, setOtp] = useState("");
  const [key, setKey] = useState(0);
  const [requestStates, setRequestStates] = useState({});

  // holds the array of achievers whose congrats popup should currently be shown, or null
  const [showCongrats, setShowCongrats] = useState(null);

  // which month tab is selected - defaults to the current month
  const [selectedMonth, setSelectedMonth] = useState(() => monthKey(new Date()));

  // ------------- dynamic, derived data (recomputed whenever `data` changes) -------------
  const customerStats = useMemo(() => buildCustomerStats(data), [data]);

  const multiServiceCustomers = useMemo(
    () => customerStats.filter((c) => c.serviceTypeCount > MULTI_SERVICE_THRESHOLD),
    [customerStats]
  );

  // fixed rolling window: current month + previous 4 months, newest first.
  // this no longer depends on what's actually in `data`, so all 5 months
  // are always selectable even if some have zero requests.
  const monthOptions = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 0; i < 5; i++) {
      months.push(monthKey(new Date(now.getFullYear(), now.getMonth() - i, 1)));
    }
    return months;
  }, []);

  const filteredData = useMemo(() => {
    if (selectedMonth === "ALL") return data;
    return data.filter((item) => {
      const d = getItemDate(item);
      return d && monthKey(d) === selectedMonth;
    });
  }, [data, selectedMonth]);

  const totals = useMemo(() => {
    const totalRequests = filteredData.length;
    const completed = filteredData.filter((d) => d.requeststatus === 3).length;
    const otpPending = filteredData.filter((d) => d.requeststatus === 2).length;
    const pending = totalRequests - completed - otpPending;
    const totalIncome = filteredData
      .filter((d) => d.requeststatus === 3)
      .reduce((sum, d) => sum + getAmount(d), 0);
    return { totalRequests, completed, otpPending, pending, totalIncome };
  }, [filteredData]);

  // as soon as the dashboard is opened, check if anyone hit the monthly goal
  useEffect(() => {
    if (!showModal) return;

    const achievers = customerStats.filter((c) => c.thisMonthCompleted >= MONTHLY_GOAL);
    if (achievers.length > 0) {
      setShowCongrats(achievers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const handleAccept = async (item, msg) => {
    try {
      const response = await axios.get(
        `http://localhost:8080/api/service/acceptRequest/${item}/${msg}`,
        {
          headers: {
            env: "local",
            token: sessionStorage.getItem("token"),
          },
        }
      );

      const result = response.data;

      if (result.success) {
        setRequestStates((prev) => ({
          ...prev,
          [item.internalKey]: "accepted",
        }));

        alert("OTP Sent Successfully");
      }
    } catch (error) {
      console.error("Accept API Error:", error);
      alert(error?.response?.data?.message || "Something went wrong");
    }
  };

  const handleConfirmClick = (item) => {
    setSelectedRequest(item);
    setKey(item.internalKey);
    setShowOtpModal(true);
  };

  const handleOtpSubmit = async () => {
    try {
      const response = await axios.get(
        `http://localhost:8080/api/service/validateOtp/${key}/${otp}`,
        {
          headers: {
            env: "local",
            token: sessionStorage.getItem("token"),
          },
        }
      );

      const result = response.data;

      if (result.responseData) {
        setRequestStates((prev) => ({
          ...prev,
          [selectedRequest.requeststatus]: 3,
        }));

        setShowOtpModal(false);
        setOtp("");
        setKey(0);
        alert("Request Completed Successfully");
      } else {
        alert("Invalid OTP");
      }
    } catch (error) {
      console.error("OTP Validation Error:", error);
      alert(error?.response?.data?.message || "Unable to validate OTP");
    }
  };

  return (
    <div className="container serviceCart">
      {/* Cart icon trigger */}
      <div className="service-card1" onClick={() => setShowModal(true)}>
        <div className="notification-badge">
          <MDBIcon
            fas
            icon="shopping-cart"
            size="sm"
            style={{
              fontSize: "28px",
              color: "#fbfbfb",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
          />

          {data.length > 0 && (
            <MDBBadge
              color="secondary"
              notification
              pill
              className="position-absolute top-0 start-100 translate-middle"
              style={{
                fontSize: "18px",
                color: "#fbfbfb",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              {data.length}
            </MDBBadge>
          )}
        </div>
      </div>

      {/* MAIN DASHBOARD */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container dashboard-container">
            <div className="modal-header">
              <h2>Service Requests Dashboard</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>

            {/* ---------- SUMMARY STAT CARDS (dynamic) ---------- */}
            <div className="stat-cards-row">
              <div className="stat-card">
                <span>Total Requests</span>
                <strong>{totals.totalRequests}</strong>
              </div>
              <div className="stat-card">
                <span>Pending</span>
                <strong>{totals.pending}</strong>
              </div>
              <div className="stat-card">
                <span>OTP Pending</span>
                <strong>{totals.otpPending}</strong>
              </div>
              <div className="stat-card">
                <span>Completed</span>
                <strong>{totals.completed}</strong>
              </div>
              <div className="stat-card highlight">
                <span>Total Income</span>
                <strong>₹{totals.totalIncome}</strong>
              </div>
            </div>

            {/* ---------- MULTI-SERVICE ALERT (dynamic) ---------- */}
            {multiServiceCustomers.length > 0 && (
              <div className="multi-service-alert">
                <h4>⚠ Customers using more than {MULTI_SERVICE_THRESHOLD} services</h4>

                {multiServiceCustomers.map((c) => (
                  <div key={c.customer} className="alert-row">
                    <div className="alert-customer">
                      <strong>{c.customer}</strong>
                      <span className="alert-services">{c.serviceTypesList.join(", ")}</span>
                    </div>

                    <div className="alert-metrics">
                      <span>
                        Total Services: <b>{c.totalServices}</b>
                      </span>
                      <span>
                        This Month: <b>{c.thisMonthCompleted}</b>
                      </span>
                      <span>
                        Income: <b>₹{c.totalIncome}</b>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="month-tabs-row">
              <select
                className="month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {monthOptions.map((m, i) => (
                  <option key={m} value={m}>
                    {i === 0 ? `${monthLabel(m)} (Current)` : monthLabel(m)}
                  </option>
                ))}
                <option value="ALL">All</option>
              </select>

              <span className="pending-chip">{totals.pending} Pending</span>
            </div>

            <div className="request-grid">
              {filteredData.length === 0 && (
                <div className="empty-state">
                  No requests found for{" "}
                  {selectedMonth === "ALL" ? "any month" : monthLabel(selectedMonth)}.
                </div>
              )}

              {filteredData.map((item) => {
                const isCompleted = item.requeststatus === 3;
                const isOtpPending = item.requeststatus === 2;

                return (
                  <div
                    key={item.internalKey}
                    className={
                      "request-card" +
                      (isCompleted ? " done" : isOtpPending ? " otp" : "")
                    }
                  >
                    <div className="request-card-top">
                      <span className="service-tag">
                        <span className="service-emoji">{getServiceIcon(item.serviceName)}</span>
                        {item.serviceName}
                      </span>

                      <span
                        className={
                          "status-badge " +
                          (isCompleted ? "badge-done" : isOtpPending ? "badge-otp" : "badge-pending")
                        }
                      >
                        {isCompleted ? "Completed" : isOtpPending ? "OTP Pending" : "Pending"}
                      </span>
                    </div>

                    <div className="request-card-body">
                      <div
                        className="customer-avatar"
                        style={{ background: getAvatarColor(item.userName || "") }}
                      >
                        {getInitials(item.userName)}
                      </div>

                      <div className="customer-info">
                        <strong>{item.userName || "Unknown Customer"}</strong>
                        <span>Personal Id: {item.personalId}</span>
                      </div>
                    </div>

                    <div className="request-card-footer">
                      <div className="footer-left">
                        <span className="request-id">Request #{item.internalKey}</span>
                        <span className="request-amount">₹{getAmount(item)}</span>
                      </div>

                      {isCompleted ? (
                        <span className="status completed-action pending">Done</span>
                      ) : isOtpPending ? (
                        <div className="action-buttons">
                          <button className="accept-btn" onClick={() => handleConfirmClick(item)}>
                            Confirm
                          </button>
                          <button
                            className="reject-btn"
                            onClick={() => handleAccept(item.internalKey, "reject")}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <div className="action-buttons">
                          <button
                            className="accept-btn"
                            onClick={() => handleAccept(item.internalKey, "Accept")}
                          >
                            Accept
                          </button>
                          <button
                            className="reject-btn"
                            onClick={() => handleAccept(item.internalKey, "reject")}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* OTP MODAL */}
      {showOtpModal && (
        <div className="modal-overlay">
          <div className="modal-container" style={{ maxWidth: "450px" }}>
            <div className="modal-header">
              <h3>OTP Verification</h3>
              <button className="close-btn" onClick={() => setShowOtpModal(false)}>
                ✕
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              <input
                type="text"
                value={otp}
                placeholder="Enter OTP"
                onChange={(e) => setOtp(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  marginBottom: "15px",
                }}
              />

              <button className="accept-btn" style={{ width: "100%" }} onClick={handleOtpSubmit}>
                Submit OTP
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONGRATS POPUP - shown automatically when the dashboard opens, if someone hit the monthly goal */}
      {showCongrats && (
        <div className="modal-overlay">
          <div className="congrats-card">
            <button className="close-btn" onClick={() => setShowCongrats(null)}>
              ✕
            </button>

            <div className="congrats-icon">🎉</div>
            <h2>Congrats!</h2>

            {showCongrats.map((c) => (
              <p key={c.customer}>
                <strong>{c.customer}</strong> completed <strong>{c.thisMonthCompleted}</strong> services
                this month!
              </p>
            ))}

            <button className="congrats-btn" onClick={() => setShowCongrats(null)}>
              Great!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceCart;
